import { ApiClient, ApiError } from "./ApiClient";
import { DOCUMENT_LIMIT_REACHED_MESSAGE } from "@/security/documentLimits.catalog";

// Contrato de la mutation saveEDocument. Mantener sincronizado con
// amplify/functions/save-edocument/types.ts ("keep in sync").
export type SaveEDocumentType =
    | "Invoice"
    | "Creditnote"
    | "Debitnote"
    | "Settlement"
    | "BuyInvoice";

export type StockMode = "reduce" | "increase" | "none";

export type SaveEDocumentParams = {
    documentType: SaveEDocumentType;
    /** Header del documento, sin sequence/numero/key: los genera la lambda. */
    document: Record<string, unknown>;
    lines: Record<string, unknown>[];
    payments?: Record<string, unknown>[];
    stockMode: StockMode;
    /** Comentario para el Productlog; admite el placeholder {numero}. */
    stockComment?: string;
};

const ERROR_MESSAGES: Record<string, string> = {
    VALIDATION: "El documento no es válido",
    INSUFFICIENT_STOCK: "No hay existencias suficientes para uno o más productos",
    SEQUENCE_CONFLICT: "No fue posible reservar el consecutivo del documento, intente de nuevo",
    TOO_MANY_ITEMS: "El documento tiene demasiados productos para guardarse de forma transaccional",
    DOCUMENT_LIMIT_REACHED: DOCUMENT_LIMIT_REACHED_MESSAGE,
    PARTIAL_FAILURE: "No fue posible guardar el documento completo",
    INTERNAL: "Error al guardar el documento",
};

/**
 * Guardado transaccional de documentos electrónicos vía lambda (camino estándar).
 * Retorna el documento persistido con la misma forma que el camino clásico
 * (id, numero, sequence, invoice_key/document_key, ...).
 */
export async function saveEDocumentViaLambda<TDoc>(params: SaveEDocumentParams): Promise<TDoc> {
    const documentId = crypto.randomUUID();

    const { data, errors } = await ApiClient.mutations.saveEDocumentMutation({
        documentType: params.documentType,
        documentId,
        document: JSON.stringify(params.document),
        lines: JSON.stringify(params.lines),
        payments: params.payments?.length ? JSON.stringify(params.payments) : undefined,
        stockMode: params.stockMode,
        stockComment: params.stockComment,
    });

    if (errors?.length) {
        throw new ApiError(`Errores al crear ${params.documentType}`, "API_ERROR", errors);
    }
    if (!data?.ok) {
        const fallback = ERROR_MESSAGES[`${data?.errorCode}`] || ERROR_MESSAGES.INTERNAL;
        throw new ApiError(data?.errorMessage || fallback, "API_ERROR");
    }

    const document = typeof data.document === "string" ? JSON.parse(data.document) : data.document;
    return (document || {}) as TDoc;
}
